import { generateInvoicePdf } from '../services/invoicePdf.js';
import { parseLocalDate } from '../services/dates.js';
import { RevenueService, RevenueMixin } from '../services/revenue.js';

export const InvoiceActivity = {
  name: 'InvoiceActivity',
  inject: ['user', 'isAuthenticated'],
  mixins: [RevenueMixin], // Add consistent revenue handling
  props: {
    userId: {
      type: String,
      default: null
    },
    height: {
      type: String,
      default: 'h-100'
    },
    showActions: {
      type: Boolean,
      default: true
    },
    maxItems: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      userJobs: [],
      jobs: [], // Alias for mixin compatibility
      invoices: [], // Same invoice data as main Invoices component
      profileData: {},
      loading: true,
      error: null,
      isGeneratingPDF: false,
      isSending: false,
      selectedJob: null,
      // Performance optimizations
      lastFetchTime: null,
      activityCache: new Map()
    };
  },
  computed: {
    cardId() {
      return `invoice-card-${this._uid}`;
    },
    
    targetUserId() {
      return this.userId || this.user?.uid;
    },
    
    invoiceJobs() {
      // Get jobs that are eligible for invoicing (completed status)
      return this.userJobs.filter(job => 
        job.status === 'completed' || this.hasInvoiceSent(job)
      );
    },
    
    recentInvoiceActivity() {
      console.log('🔍 Invoice Activity: Processing invoices...', this.invoices?.length || 0);

      if (!this.invoices || this.invoices.length === 0) {
        console.log('🔍 Invoice Activity: No invoices available');
        return [];
      }

      // Convert invoice data to activity format, sorted by most recent first
      const activities = this.invoices
        .map(invoice => ({
          id: `${invoice.jobId}-${invoice.id}`,
          job: invoice.job || { id: invoice.jobId },
          type: invoice.status === 'pending' ? 'pending' : 'sent',
          customerName: invoice.customerName || 'Unknown Customer',
          amount: this.formatCurrency(invoice.amount || invoice.total || 0),
          date: parseLocalDate(invoice.dateSent || invoice.dateCreated) || new Date(),
          status: invoice.status,
          invoiceNumber: invoice.invoiceNumber,
          invoiceId: invoice.id,
          resendCount: invoice.resendCount || 0
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, this.maxItems);

      console.log('🔍 Invoice Activity: Generated activities:', activities.length);
      return activities;
    },
    
    // Updated to use consistent revenue calculation
    totalInvoiceRevenue() {
      return this.formattedRevenue.actual; // Only PAID invoices
    },

    // Add pending and outstanding amounts
    totalPendingRevenue() {
      return this.formattedRevenue.pending;
    },

    totalOutstandingRevenue() {
      return this.formattedRevenue.outstanding;
    },

    // Revenue breakdown for dashboard display
    activitySummary() {
      return {
        totalRevenue: this.formattedRevenue.actual, // Only PAID invoices
        pendingRevenue: this.formattedRevenue.pending, // Sent but not paid
        outstandingRevenue: this.formattedRevenue.outstanding, // All unpaid
        totalInvoices: this.revenueStats.totalInvoices,
        paidInvoices: this.revenueStats.paidInvoices,
        pendingInvoices: this.revenueStats.pendingInvoices
      };
    },
    
    pendingInvoiceCount() {
      return this.recentInvoiceActivity.filter(activity => activity.status === 'pending').length;
    },
    
    sentInvoiceCount() {
      return this.recentInvoiceActivity.filter(activity => activity.status === 'sent' || activity.status === 'paid').length;
    }
  },
  
  methods: {
    // Fetch invoice data using same logic as main Invoices component
    async fetchInvoices() {
      console.log('📋 Invoice Activity: Starting fetchInvoices...');

      try {
        if (!this.targetUserId) {
          console.log('Invoice Activity: No target user ID');
          this.invoices = [];
          return;
        }

        // Fetch user's jobs
        const jobsSnapshot = await firebase.database()
          .ref('jobs')
          .orderByChild('userId')
          .equalTo(this.targetUserId)
          .once('value');

        const jobs = jobsSnapshot.val() || {};
        console.log(`📋 Invoice Activity: Found ${Object.keys(jobs).length} jobs for user`);

        const uniqueInvoices = [];
        const invoiceMap = new Map();

        Object.entries(jobs).forEach(([jobId, job]) => {
          if (!job) return;

          if (job.invoices && Object.keys(job.invoices).length > 0) {
            // Process invoices for this job using same logic as main component
            const sortedInvoices = Object.entries(job.invoices)
              .sort(([, a], [, b]) => (b.sentDate || 0) - (a.sentDate || 0));

            const [latestInvoiceId, latestInvoiceData] = sortedInvoices[0];

            // Apply same status processing logic
            const rawStatus = latestInvoiceData.status;
            const normalizedStatus = (rawStatus || 'sent').toLowerCase();
            const validStatus = Object.values(RevenueService.INVOICE_STATUSES).includes(normalizedStatus)
              ? normalizedStatus
              : RevenueService.INVOICE_STATUSES.SENT;

            // Check if invoice is overdue (pending/sent > 7 days ago without payment)
            const sentDate = new Date(latestInvoiceData.sentDate || latestInvoiceData.dateCreated);
            const daysSinceSent = (Date.now() - sentDate.getTime()) / (1000 * 60 * 60 * 24);
            const isOverdue = (validStatus === RevenueService.INVOICE_STATUSES.SENT ||
                              validStatus === RevenueService.INVOICE_STATUSES.PENDING) &&
                              daysSinceSent > 7;

            const finalStatus = isOverdue ? RevenueService.INVOICE_STATUSES.OVERDUE : validStatus;

            const invoice = {
              id: latestInvoiceId,
              jobId: jobId,
              job: job,
              customerName: job.customerName || 'Unknown Customer',
              customerEmail: job.customerEmail || '',
              serviceType: job.serviceType || 'Service',
              amount: this.parseAmount(latestInvoiceData.amount || latestInvoiceData.total || job.quotedPrice),
              total: this.parseAmount(latestInvoiceData.amount || latestInvoiceData.total || job.quotedPrice),
              status: finalStatus,
              dateCreated: latestInvoiceData.dateCreated || RevenueService.toLocalDateString(latestInvoiceData.sentDate),
              dateSent: RevenueService.toLocalDateString(latestInvoiceData.sentDate),
              datePaid: RevenueService.toLocalDateString(latestInvoiceData.datePaid),
              invoiceNumber: latestInvoiceData.invoiceNumber || RevenueService.generateInvoiceNumber(jobId, latestInvoiceId),
              resendCount: sortedInvoices.length - 1,
              isOverdue: isOverdue
            };

            invoiceMap.set(jobId, invoice);
          } else if (job.status === 'completed') {
            // Pending invoice (job completed but no invoice sent)
            const invoice = {
              id: 'pending',
              jobId: jobId,
              job: job,
              customerName: job.customerName || 'Unknown Customer',
              customerEmail: job.customerEmail || '',
              serviceType: job.serviceType || 'Service',
              amount: this.parseAmount(job.quotedPrice),
              total: this.parseAmount(job.quotedPrice),
              status: RevenueService.INVOICE_STATUSES.PENDING,
              dateCreated: job.dateSubmitted || new Date().toISOString().split('T')[0],
              dateSent: null,
              datePaid: null,
              invoiceNumber: RevenueService.generateInvoiceNumber(jobId, 'pending'),
              resendCount: 0,
              isOverdue: false
            };

            invoiceMap.set(jobId, invoice);
          }
        });

        this.invoices = Array.from(invoiceMap.values())
          .sort((a, b) => {
            const dateA = new Date(a.dateSent || a.dateCreated || 0);
            const dateB = new Date(b.dateSent || b.dateCreated || 0);
            return dateB - dateA;
          });

        console.log(`📋 Invoice Activity: Processed ${this.invoices.length} invoices`);

      } catch (error) {
        console.error('Invoice Activity: Error fetching invoices:', error);
        this.error = error.message;
        this.invoices = [];
      }
    },

    async fetchUserData() {
      this.loading = true;
      this.error = null;
      
      try {
        if (!this.targetUserId) {
          console.log('No user UID found');
          this.loading = false;
          return;
        }

        console.log('🔄 Fetching user data for:', this.targetUserId);

        // Fetch user profile data
        const userRef = firebase.database().ref(`users/${this.targetUserId}`);
        const userSnapshot = await userRef.once('value');

        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          this.profileData = {
            email: userData.email || this.user?.email,
            companyName: userData.companyName || '',
            userName: userData.userName || '',
            websiteURL: userData.websiteURL || '',
            instagramURL: userData.instagramURL || '',
            twitterURL: userData.twitterURL || ''
          };
        }

        // Fetch jobs data
        const jobsRef = firebase.database().ref('jobs');
        const jobsQuery = jobsRef.orderByChild('userId').equalTo(this.targetUserId);
        const jobsSnapshot = await jobsQuery.once('value');

        if (jobsSnapshot.exists()) {
          const jobs = [];
          const processedJobs = new Set(); // Prevent duplicate jobs
          
          jobsSnapshot.forEach((childSnapshot) => {
            const jobId = childSnapshot.key;
            
            // Skip if we've already processed this job
            if (processedJobs.has(jobId)) {
              console.warn('🚨 Duplicate job detected and skipped:', jobId);
              return;
            }
            
            processedJobs.add(jobId);
            
            const job = childSnapshot.val();
            job.id = jobId;
            
            // Ensure photos are arrays during initial load
            if (job.photos) {
              ['before', 'after'].forEach(type => {
                if (job.photos[type]) {
                  // Convert Firebase pseudo-arrays to real arrays
                  job.photos[type] = Object.values(job.photos[type]);
                }
              });
            }
            
            jobs.push(job);
          });

          this.userJobs = jobs.sort((a, b) => {
            const dateA = new Date(`${a.dateSubmitted} ${a.timeSubmitted}`);
            const dateB = new Date(`${b.dateSubmitted} ${b.timeSubmitted}`);
            return dateB - dateA;
          });

          this.jobs = this.userJobs; // For mixin compatibility

          console.log(`✅ Loaded ${this.userJobs.length} unique jobs`);
        }

        // Fetch invoice data using the same logic as main Invoices component
        await this.fetchInvoices();

        this.lastFetchTime = Date.now();
        this.activityCache.clear(); // Clear cache when data changes

        // Clear revenue cache when new data is loaded
        this.clearRevenueCache();

      } catch (error) {
        console.error('❌ Error fetching user data:', error);
        this.error = 'Failed to load invoice data. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    hasInvoiceSent(job) {
      return job.invoices && Object.keys(job.invoices).length > 0;
    },
    
    getLatestInvoiceDate(job) {
      if (!job.invoices) return null;
      
      const invoices = Object.values(job.invoices);
      const latestInvoice = invoices.sort((a, b) => b.sentDate - a.sentDate)[0];
      // Returns null rather than an Invalid Date: invoices with no sentDate
      // otherwise rendered the literal string "Invalid Date" into the job list.
      return latestInvoice ? parseLocalDate(latestInvoice.sentDate) : null;
    },
    
    // Helper method to safely parse amounts
    parseAmount(priceString) {
      if (!priceString) return 0;
      const cleaned = priceString.toString().replace(/[$,]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    },
    
    // Use consistent invoice number generation from RevenueService
    generateInvoiceNumber(jobId, invoiceId) {
      return RevenueService.generateInvoiceNumber(jobId, invoiceId);
    },
    
    formatActivityDate(date) {
      if (!date) return '';
      
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
    },
    
    formatCurrency(amount) {
      const numericAmount = this.parseAmount(amount);
      return numericAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      });
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        sent: 'alert-primary',
        pending: 'alert-warning text-dark',
        paid: 'alert-success',
        overdue: 'alert-danger'
      };
      return `badge ${classes[status] || 'bg-secondary'}`;
    },
    
    getActivityIcon(status) {
      const icons = {
        sent: 'bi-send',
        pending: 'bi-clock',
        paid: 'bi-currency-dollar',
        overdue: 'bi-exclamation-triangle-fill'
      };
      return icons[status] || 'bi-file-earmark-text';
    },
    
    // Delegates to the shared generator; see src/services/invoicePdf.js.
    async generatePDF(job) {
      const invoice = (job && job.invoices)
        ? Object.values(job.invoices).filter(Boolean).sort(
            (a, b) => (b.sentDate || 0) - (a.sentDate || 0)
          )[0] || {}
        : {};
      const { doc } = await generateInvoicePdf({
        job: job || {},
        invoice,
        uid: this.targetUserId || (this.user && this.user.uid),
      });
      return doc;
    },
    
    async previewInvoice(activity) {
      this.selectedJob = activity.job;
      this.isGeneratingPDF = true;
      
      try {
        const pdfDoc = await this.generatePDF(activity.job);
        const pdfDataUri = pdfDoc.output('datauristring');
        
        // Open PDF in modal or new window
        const previewWindow = window.open();
        previewWindow.document.write(`
          <iframe width="100%" height="100%" src="${pdfDataUri}"></iframe>
        `);
        
      } catch (error) {
        console.error('❌ Error previewing invoice:', error);
        alert('Error generating invoice preview. Please try again.');
      } finally {
        this.isGeneratingPDF = false;
      }
    },
    
    async sendInvoice(activity) {
      this.selectedJob = activity.job;
      this.isSending = true;
      
      try {
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          throw new Error('No authenticated user found');
        }
        
        const pdfDoc = await this.generatePDF(activity.job);
        const pdfBase64 = pdfDoc.output('datauristring');
        const base64Content = pdfBase64.split(',')[1];
        
        const payload = {
          pdfBase64: base64Content,
          jobId: activity.job.id,
          customerEmail: activity.job.customerEmail,
          customerName: activity.job.customerName,
          invoiceNumber: activity.invoiceNumber,
          userId: currentUser.uid,
          vendor: activity.job.vendor || this.profileData?.companyName || 'Company Name'
        };
        
        const sendInvoiceEmail = firebase.functions().httpsCallable('sendInvoiceEmail');
        const result = await sendInvoiceEmail(payload);
        
        if (!result.data?.success) {
          throw new Error(result.data?.error || 'Failed to send invoice');
        }
        
        // Create standardized invoice with complete data
        const invoiceResult = await RevenueService.createInvoice(activity.job, currentUser.uid, 'sent');
        if (!invoiceResult.success) {
          throw new Error(`Failed to create invoice: ${invoiceResult.error}`);
        }
        
        // Clear cache and emit event
        this.activityCache.clear();
        this.$emit('invoice-sent', activity.job);
        
        alert('✅ Invoice sent successfully!');
        
        // Refresh data to show updated status
        await this.fetchUserData();
        
      } catch (error) {
        console.error('❌ Error sending invoice:', error);
        alert('Error sending invoice. Please try again.');
      } finally {
        this.isSending = false;
      }
    },
    
    formatDate(dateString) {
      const date = parseLocalDate(dateString);
      if (!date) return '';
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    
    viewJobDetails(activity) {
      this.$emit('view-job', activity.job);
    },
    
    // Manual refresh method
    async refreshData() {
      this.activityCache.clear();
      await this.fetchUserData();
    }
  },
  
  async mounted() {
    console.log('🚀 Invoice Activity mounted');
    await this.fetchUserData();
  },
  
  // Cleanup on unmount
  beforeUnmount() {
    this.activityCache.clear();
  },
  
  template: `
    <div class="mb-0 h-100">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0 fw-bold">
          Invoice activity
        </h6>
        <div class="d-flex gap-2">
          <button @click="refreshData" class="btn btn-text p-2" :disabled="loading">
            <i class="bi bi-arrow-clockwise"></i>
          </button>
          <div class="d-none d-flex gap-1" v-if="!loading && !error">
            <span class="badge alert-info">{{ sentInvoiceCount }} Sent</span>
            <span class="badge alert-warning text-dark" v-if="pendingInvoiceCount > 0">
              {{ pendingInvoiceCount }} Pending
            </span>
          </div>
        </div>
      </div>
      
      <div class="card-body p-0">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <div class="mt-2 text-muted">Loading invoice data...</div>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger m-3">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ error }}
          <button @click="fetchUserData" class="btn btn-sm btn-outline-danger ms-2">
            <i class="bi bi-arrow-clockwise "></i> Retry
          </button>
        </div>
        
        <!-- Content -->
        <div v-else>
          <!-- Recent Activity List -->
          <div v-if="recentInvoiceActivity.length > 0">
            <div class="list-group list-group-flush">
              <div 
                v-for="activity in recentInvoiceActivity" 
                :key="activity.id"
                class="list-group-item p-4 border-0"
                style="border-bottom: 1px solid #dee2e6 !important;"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="d-flex align-items-center gap-2">
                        <i :class="'text-' + (activity.status === 'pending' ? 'warning' : activity.status === 'paid' ? 'success' : 'primary') + ' ' + getActivityIcon(activity.status)"></i>
                        <span class="fw-bold">{{ formatCurrency(activity.amount) }}</span>
                        <span :class="getStatusBadgeClass(activity.status)">
                          {{ activity.status.charAt(0).toUpperCase() + activity.status.slice(1) }}
                        </span>
                      </div>
                      <span class="small text-muted">{{ formatActivityDate(activity.date) }}</span>
                    </div>
                    
                    <div class="mb-1">
                      <span class="fw-medium">{{ activity.customerName }}</span>
                    </div>
                    
                    <div class="small text-muted mb-0">
                      {{ activity.job.serviceType }} • Invoice #{{ activity.invoiceNumber }}
                    </div>
                  </div>
                </div>
                
                <!-- Action Buttons -->
                <div v-if="showActions" class="mt-3 d-none">
                  <div class="btn-group btn-group-sm" role="group">
                    <button 
                      @click="viewJobDetails(activity)"
                      class="btn btn-outline-primary"
                      type="button"
                      title="View Job Details"
                    >
                      <i class="bi bi-eye"></i> View
                    </button>
                    
                    <button 
                      @click="previewInvoice(activity)"
                      :disabled="isGeneratingPDF"
                      class="btn btn-outline-secondary"
                      type="button"
                      title="Preview PDF"
                    >
                      <i class="bi bi-file-earmark-pdf"></i> 
                      <span v-if="isGeneratingPDF">...</span>
                      <span v-else>PDF</span>
                    </button>
                    
                    <button 
                      v-if="activity.status === 'pending'"
                      @click="sendInvoice(activity)"
                      :disabled="isSending"
                      class="btn btn-success"
                      type="button"
                      title="Send Invoice"
                    >
                      <span v-if="isSending">
                        <span class="spinner-border spinner-border-sm me-1"></span>
                        Sending...
                      </span>
                      <span v-else>
                        <i class="bi bi-send"></i> Send
                      </span>
                    </button>
                    
                    <button 
                      v-else-if="activity.status === 'sent'"
                      @click="sendInvoice(activity)"
                      :disabled="isSending"
                      class="btn btn-outline-success"
                      type="button"
                      title="Resend Invoice"
                    >
                      <i class="bi bi-arrow-repeat"></i> Resend
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- No Activity State -->
          <div v-else class="text-center py-5">
            <i class="bi bi-receipt display-4 text-muted mb-3"></i>
            <h6 class="text-muted">No Invoice Activity</h6>
            <p class="small text-muted mb-0">
              Complete jobs to start generating invoices
            </p>
          </div>
        </div>
      </div>
      
      <!-- Footer Actions -->
      <div class="card-footer bg-transparent border-0 p-3" v-if="showActions && recentInvoiceActivity.length > 0">
        <div class="d-flex justify-content-center align-items-center">
          <router-link
            to="/invoices"
            @click="$emit('view-all-invoices')"
            class="btn btn-link text-primary text-decoration-none p-0"
          >
            Manage invoices
          </router-link>
          
          
        </div>
      </div>
    </div>
  `
};
